/**
 * ProfileShareSheet (web): the share dialog for a public page — a company
 * hiring page in company mode, a personal public profile otherwise.
 *
 * Structure follows the CV share sheet (`components/cv/CvShareSheet.web.tsx`)
 * so the two read as one feature: a bordered link card, a row of square action
 * tiles, then the sections below it. Views:
 *  - main        — link card, action tiles, one share-platform rail
 *  - qr          — QR preview + download
 *  - bio:<id>    — per-platform detail: bio walkthrough and/or post composer
 *
 * There used to be a second "Share to" grid below the rail. It showed the same
 * logos twice — five of its six tiles duplicated something already in the rail
 * — so both actions now live behind one tile per platform.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import {
  getLinkInBioPlatform,
  isLinkInBioPlatformId,
  type LinkInBioPlatform,
} from '../../lib/link-in-bio';
import { AddToBioGuide, SharePlatformRail } from './AddToBioGuide.web';
import { Alert } from '../../lib/alert.web';
import { writeToClipboard } from '../../lib/clipboard.web';
import {
  withCvShareRef,
  withOwnerViewMarker,
} from '@tabbio/utils/analytics/cv-traffic';

/** `bio:<platform>` keeps the platform detail views in one flat view union. */
const BIO_PREFIX = 'bio:';

type View = 'main' | 'qr' | `bio:${string}`;

/** Returns the platform a detail view refers to, or null for other views. */
function bioPlatformFor(view: View) {
  if (!view.startsWith(BIO_PREFIX)) return null;
  const id = view.slice(BIO_PREFIX.length);
  return isLinkInBioPlatformId(id) ? (getLinkInBioPlatform(id) ?? null) : null;
}

export function ProfileShareSheet() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const tx = useCallback((key: string) => String(t(key as never)), [t]);

  const { routeMode, publicUrl, draftProfile, username, confirmCompanyShare } =
    usePublicProfileBuilderModel();
  const isCompany = routeMode === 'company';
  const profileRoutes = useMemo(
    () => getPublicProfileRoutes(routeMode),
    [routeMode],
  );

  const [view, setView] = useState<View>('main');
  const [copied, setCopied] = useState(false);
  const [signatureCopied, setSignatureCopied] = useState(false);

  const url = publicUrl || '';
  const ownerName = (draftProfile?.name ?? '').trim() || username || 'Tabbio';
  // One-line email signature: name + role line + page URL (PRD §13).
  const signatureRole =
    (draftProfile?.tagline ?? '').trim() || (draftProfile?.bio ?? '').trim();
  // Links handed out carry `?ref=<channel>` so analytics can name where a
  // visit came from; the URL shown on screen stays clean.
  const emailSignature = [ownerName, signatureRole, url ? withCvShareRef(url, 'email') : '']
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' · ');

  const bioPlatform = bioPlatformFor(view);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  const onBack = useCallback(() => {
    if (view !== 'main') {
      setView('main');
      return;
    }
    closeDialog();
  }, [closeDialog, view]);

  const reportCopyFailure = useCallback(() => {
    Alert.alert(
      tx('publicProfile.shareSheet.copyFailed'),
      tx('publicProfile.shareSheet.copyFailedHint'),
    );
  }, [tx]);

  // `channel` is the platform when the copy is for a bio guide's paste.
  const handleCopy = useCallback(async (channel: string = 'copy') => {
    if (!url) return;
    if (!(await writeToClipboard(withCvShareRef(url, channel)))) {
      reportCopyFailure();
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
    confirmCompanyShare();
  }, [confirmCompanyShare, reportCopyFailure, url]);

  const handleCopyEmailSignature = useCallback(async () => {
    if (!emailSignature) return;
    if (!(await writeToClipboard(emailSignature))) {
      reportCopyFailure();
      return;
    }
    setSignatureCopied(true);
    window.setTimeout(() => setSignatureCopied(false), 1800);
    confirmCompanyShare();
  }, [confirmCompanyShare, emailSignature, reportCopyFailure]);

  const handleOpenPage = useCallback(() => {
    if (!url || typeof window === 'undefined') return;
    // The owner marker keeps the owner's own visit out of their view count.
    window.open(withOwnerViewMarker(url), '_blank', 'noopener,noreferrer');
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ url: withCvShareRef(url, 'share') });
        confirmCompanyShare();
        return;
      } catch {
        /* dismissed — fall through to copy */
      }
    }
    void handleCopy();
  }, [confirmCompanyShare, handleCopy, url]);

  const openPostComposer = useCallback(
    (platform: LinkInBioPlatform) => {
      if (!platform.shareIntent || !url || typeof window === 'undefined') return;
      const target = platform.shareIntent(
        encodeURIComponent(withCvShareRef(url, platform.id)),
      );
      window.open(target, '_blank', 'noopener,noreferrer');
      confirmCompanyShare();
    },
    [confirmCompanyShare, url],
  );

  /**
   * A platform with no bio walkthrough (Telegram) has nothing to show in a
   * detail view, so its tile composes a post straight away rather than making
   * the user click through a page that only holds one button.
   */
  const handleSelectPlatform = useCallback(
    (platform: LinkInBioPlatform) => {
      if (!platform.stepsKey) {
        openPostComposer(platform);
        return;
      }
      setView(`${BIO_PREFIX}${platform.id}`);
    },
    [openPostComposer],
  );

  const title = bioPlatform
    ? tx(bioPlatform.labelKey)
    : view === 'qr'
      ? tx('publicProfile.shareSheet.qrTitle')
      : tx(
          isCompany
            ? 'publicProfile.shareSheet.company.title'
            : 'publicProfile.shareSheet.title',
        );
  const subtitle = bioPlatform
    ? tx('linkInBio.guideSubtitle')
    : view === 'qr'
      ? tx(
          isCompany
            ? 'publicProfile.shareSheet.company.qrSubtitle'
            : 'publicProfile.shareSheet.qrSubtitle',
        )
      : tx(
          isCompany
            ? 'publicProfile.shareSheet.company.subtitle'
            : 'publicProfile.shareSheet.subtitle',
        );

  return (
    <WebDialog
      title={title}
      subtitle={subtitle}
      onClose={closeDialog}
      onBack={view !== 'main' ? onBack : undefined}
      maxWidth={560}
    >
      {view === 'main' && (
        <MainView
          url={url}
          copied={copied}
          tx={tx}
          onCopy={() => void handleCopy()}
          onOpenQr={() => setView('qr')}
          onNativeShare={() => void handleNativeShare()}
          isCompany={isCompany}
          signatureCopied={signatureCopied}
          onOpenPage={handleOpenPage}
          onCopyEmailSignature={() => {
            void handleCopyEmailSignature();
          }}
          onSelectPlatform={handleSelectPlatform}
        />
      )}
      {view === 'qr' && (
        <QrView url={url} tx={tx} onDownloaded={confirmCompanyShare} />
      )}
      {bioPlatform ? (
        <AddToBioGuide
          platform={bioPlatform}
          url={url}
          copied={copied}
          onCopy={() => void handleCopy(bioPlatform.id)}
          onPost={
            bioPlatform.shareIntent
              ? () => openPostComposer(bioPlatform)
              : undefined
          }
        />
      ) : null}
    </WebDialog>
  );
}

function MainView({
  url,
  copied,
  tx,
  onCopy,
  onOpenQr,
  onNativeShare,
  isCompany,
  signatureCopied,
  onOpenPage,
  onCopyEmailSignature,
  onSelectPlatform,
}: {
  url: string;
  copied: boolean;
  tx: (key: string) => string;
  onCopy: () => void;
  onOpenQr: () => void;
  onNativeShare: () => void;
  isCompany: boolean;
  signatureCopied: boolean;
  onOpenPage: () => void;
  onCopyEmailSignature: () => void;
  onSelectPlatform: (platform: LinkInBioPlatform) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <LinkCard
        url={url}
        copied={copied}
        tx={tx}
        onCopy={onCopy}
        onShare={onNativeShare}
        label={tx(
          isCompany
            ? 'publicProfile.shareSheet.company.pageLinkLabel'
            : 'publicProfile.shareSheet.pageLinkLabel',
        )}
      />

      {/* Square tiles, matching the CV share sheet's three-up row. Company
          mode gets the email signature; a personal page has no use for it. */}
      <div style={tiles}>
        <Tile
          icon="qr_code_2"
          label={tx('publicProfile.shareSheet.menu.qrCode')}
          onPress={onOpenQr}
        />
        {isCompany ? (
          <Tile
            icon={signatureCopied ? 'check' : 'alternate_email'}
            label={
              signatureCopied
                ? tx('publicProfile.shareSheet.company.signatureCopied')
                : tx('publicProfile.shareSheet.company.emailSignature')
            }
            onPress={onCopyEmailSignature}
          />
        ) : null}
        <Tile
          icon="open_in_new"
          label={tx(
            isCompany
              ? 'publicProfile.shareSheet.company.openPage'
              : 'publicProfile.shareSheet.menu.open',
          )}
          onPress={onOpenPage}
        />
      </div>

      {/* The reason this dialog exists: most people never get the link off this
          screen and into the place candidates actually look. */}
      <SharePlatformRail
        audience={isCompany ? 'company' : 'personal'}
        onSelect={onSelectPlatform}
      />
    </div>
  );
}

function QrView({
  url,
  tx,
  onDownloaded,
}: {
  url: string;
  tx: (key: string) => string;
  onDownloaded?: () => void;
}) {
  const handleDownload = useCallback(() => {
    if (typeof document === 'undefined') return;
    const canvas = document.querySelector(
      'canvas[data-tabbio-qr]',
    ) as HTMLCanvasElement | null;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = 'tabbio-qr.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onDownloaded?.();
  }, [onDownloaded]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
      <div
        style={{
          padding: 18,
          borderRadius: 24,
          background: 'rgba(0,0,0,0.04)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {url ? (
          <QRCodeCanvas
            value={withCvShareRef(url, 'qr')}
            size={220}
            level="H"
            includeMargin={false}
            data-tabbio-qr=""
            bgColor="transparent"
            fgColor="var(--text-primary)"
          />
        ) : (
          <div
            style={{
              width: 220,
              height: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-tertiary)',
              fontSize: 13,
            }}
          >
            No URL configured yet.
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-tertiary)',
          textAlign: 'center',
          maxWidth: 360,
          lineHeight: '18px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {url}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <NeutralButton onPress={handleDownload} icon="download" label={tx('publicProfile.shareSheet.qr.download')} primary />
      </div>
    </div>
  );
}

/**
 * The link row, matching `CvShareSheet.web.tsx`'s `linkCard`: a bordered card
 * with a small label above the URL and icon-only actions, rather than the
 * rounded pill with a wide text button this sheet used to carry.
 */
function LinkCard({
  url,
  copied,
  tx,
  onCopy,
  onShare,
  label,
}: {
  url: string;
  copied: boolean;
  tx: (key: string) => string;
  onCopy: () => void;
  onShare?: () => void;
  label: string;
}) {
  const display = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return (
    <div style={linkCard}>
      <div style={linkRow}>
        <MaterialSymbol
          name="link"
          size={18}
          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
        />
        <span style={linkCopyCol}>
          <span style={linkLabel}>{label}</span>
          <span style={linkText} dir="ltr">
            {display || 'N/A'}
          </span>
        </span>
        {onShare ? (
          <button
            type="button"
            onClick={onShare}
            aria-label={tx('publicProfile.shareSheet.shareVia')}
            style={iconBtn}
          >
            <MaterialSymbol name="ios_share" size={18} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCopy}
          aria-label={tx('publicProfile.shareSheet.copyLink')}
          style={copyBox}
        >
          <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={16} />
        </button>
      </div>
    </div>
  );
}

/** Square action tile, matching the CV share sheet's three-up row. */
function Tile({
  icon,
  label,
  onPress,
}: {
  icon: string;
  label: string;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...tile,
        background: hover ? 'var(--hover-strong, #e9e8ef)' : 'var(--hover, #f3f2f8)',
      }}
    >
      <MaterialSymbol name={icon} size={22} />
      <span style={tileLabel}>{label}</span>
    </button>
  );
}

function NeutralButton({
  icon,
  label,
  onPress,
  primary = false,
}: {
  icon: string;
  label: string;
  onPress: () => void;
  primary?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        height: 44,
        padding: '0 18px',
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: primary
          ? `var(--text-primary)`
          : hover
            ? 'rgba(0,0,0,0.06)'
            : 'rgba(0,0,0,0.04)',
        color: primary ? 'var(--bg)' : 'var(--text-primary)',
        opacity: primary && hover ? 0.88 : 1,
        fontSize: 14,
        fontWeight: 500,
        transition: 'background-color 140ms, opacity 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <MaterialSymbol name={icon} size={16} />
      {label}
    </button>
  );
}

// ─── Styles (mirrors CvShareSheet.web.tsx) ───────────────────────────────

const FONT = "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif";

const linkCard: React.CSSProperties = {
  border: '1px solid var(--input-border)',
  borderRadius: 16,
  padding: '8px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  fontFamily: FONT,
};
const linkRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 52,
};
const linkCopyCol: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const linkLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  lineHeight: '14px',
};
const linkText: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 16,
  fontWeight: 500,
  color: 'var(--text-primary)',
};
const iconBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: 0,
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text-primary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const copyBox: React.CSSProperties = {
  ...iconBtn,
  border: '1px solid var(--input-border)',
};
const tiles: React.CSSProperties = { display: 'flex', gap: 12 };
const tile: React.CSSProperties = {
  flex: 1,
  // The CV sheet's tiles are square because it always renders three of them.
  // This sheet renders two in personal mode, where a 1:1 ratio would stretch
  // each tile to ~420px, so the height is fixed at roughly what three-up gives.
  height: 132,
  borderRadius: 8,
  border: 0,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  color: 'var(--text-primary)',
  transition: 'background-color 140ms',
  fontFamily: FONT,
};
const tileLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: -0.12,
  color: 'var(--text-primary)',
  textAlign: 'center',
  paddingInline: 6,
};
